import React, { useRef } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import PageContainer from '../../components/containers/PageContainer';
import { Column } from '../../components/table/DataTable';
import { theme } from '../../assets/styles/theme';
import { messageService } from '../../services/Message/messageService';
import { MESSAGES_TAB_ONGOING_PATH } from '../../providers/messages/messagesPaths';
import { UserDetailsDto } from '../../services/User/userService.dto';
import { authService } from '../../services/Auth/authService';
import { userService } from '../../services/User/userService';
import { TableFilter } from '../../components/table/FiltersPanel';
import DataTableWithFilters from '../../components/table/DataTableWithFilters';
import { useGlobalData } from '../../providers/GlobalDataProvider';
import { DataTableRef } from '../../components/table/dataTableRef';
import { dateUtils } from '../../utils/dateUtils';
import CellWithPopover from '../../components/table/CellWithPopover';
import { useError } from '../../providers/useError';
import { usePopup } from '../../providers/PopupProvider';
import VerificationPopover, { VerificationMessages } from './components/VerificationPopover';
import BlockUserPopover from './components/BlockUserPopover';
import UserActions from './components/UserActions';
import UserDetailsCard from './components/UserDetailsCard';
import { useUnblockUser } from './hooks/useUnblockUser';
import { useUserFilters } from './hooks/useUserFilters';
import UnblockUserPopover from './components/UnblockUserPopover';
import PhoneNumberCell from './components/PhoneNumberCell';
import EmailCell from './components/EmailCell';

const Container = styled.div`
  padding: 0 ${theme.paddingStandard}px;
  flex-direction: column;
  display: flex;
  width: 100%;
`;

const mapUserStatus = (user: UserDetailsDto) => {
  if (user.userStatus === 'DELETED') {
    return 'Deleted';
  } else if (user.blocked) {
    return 'Blocked';
  } else if (user.userStatus === 'NEW') {
    return 'New';
  } else {
    return 'Active';
  }
};

const FindUser = () => {
  const { t } = useTranslation('users');
  const { blockadeReasons } = useGlobalData();
  const dataTableRef = useRef<DataTableRef>(null);
  const navigate = useNavigate();
  const { handleError } = useError();
  const { unblockUser } = useUnblockUser();
  const { onTableFiltered, filterInitState } = useUserFilters();
  const { showPopover, hidePopover } = usePopup();

  const columns: Column<UserDetailsDto>[] = [
    {
      id: 'id',
      title: t('columns.id'),
      isSortable: true,
      align: 'center',
      renderCell: data => <CellWithPopover popover={<UserDetailsCard userInfo={data} />}>{data.id}</CellWithPopover>,
    },
    {
      id: 'email',
      title: t('columns.username'),
      isSortable: true,
      minWidth: 100,
      maxWidth: 200,
      renderCell: data => <EmailCell user={data} onUpdated={() => dataTableRef.current?.refresh()} />,
    },
    {
      id: 'firstName',
      title: t('columns.first-name'),
      minWidth: 100,
      maxWidth: 200,
      isSortable: true,
      renderCell: data => data.firstName,
    },
    {
      id: 'lastName',
      title: t('columns.last-name'),
      minWidth: 100,
      maxWidth: 200,
      isSortable: true,
      renderCell: data => data.lastName,
    },
    {
      id: 'phoneNumber',
      title: t('columns.phone-number'),
      renderCell: data => <PhoneNumberCell user={data} onUpdated={() => dataTableRef.current?.refresh()} />,
    },
    {
      id: 'fundraiserVerified',
      title: t('columns.verified'),
      renderCell: data => (data.fundraiserVerified ? t('common:yes') : t('common:no')),
    },
    {
      id: 'localization',
      title: t('columns.localization'),
      renderCell: data => data.location?.city,
    },
    {
      id: 'zipCode',
      title: t('columns.zip-code'),
      renderCell: data => data.location?.zip,
    },
    {
      id: 'created',
      title: t('columns.creation-date'),
      renderCell: data => dateUtils.toLocalDateTimeString(data.created),
      isSortable: true,
    },
    {
      id: 'userStatus',
      title: t('columns.status'),
      renderCell: mapUserStatus,
    },
    {
      id: 'actions',
      title: t('columns.actions'),
      align: 'center',
      minWidth: 100,
      renderCell: data => {
        return (
          <UserActions
            user={data}
            onBlockPressed={element => {
              showPopover(
                <BlockUserPopover
                  userData={data}
                  blockadeReasons={blockadeReasons}
                  onBlockPressed={(reason, comment) => handleBlockUser(data.id, reason, comment)}
                />,
                element
              );
            }}
            onUnblockPressed={element => {
              showPopover(
                <UnblockUserPopover userData={data} onUnblockPressed={() => handleUnblockUser(data.id)} />,
                element
              );
            }}
            onContactUser={() => openConversation(data.id)}
            onPinVerification={showPinPopup}
            onConfirmSignup={confirmUserSignup}
          />
        );
      },
    },
  ];

  const filters: TableFilter[] = [
    { type: 'numeric', name: 'id', label: t('filters.id') },
    { type: 'text', name: 'email', label: t('filters.email') },
    { type: 'text', name: 'phoneNumber', label: t('filters.phone-number') },
    { type: 'text', name: 'firstName', label: t('filters.first-name') },
    { type: 'text', name: 'lastName', label: t('filters.last-name') },
    { type: 'text', name: 'localization', label: t('filters.localization') },
    { type: 'text', name: 'zipCode', label: t('filters.zip-code') },
  ];

  const handleBlockUser = (userId: number, reason: string, comment?: string) => {
    userService.blockUser(userId, reason, comment).then(() => dataTableRef.current?.refresh());
    hidePopover();
  };

  const handleUnblockUser = (userId: number) => {
    unblockUser(userId, dataTableRef.current?.refresh);
    hidePopover();
  };

  const openConversation = (endUserId: number) =>
    messageService
      .createConversation(endUserId)
      .then(({ data }) => navigate(`${MESSAGES_TAB_ONGOING_PATH}/${data.id}`));

  const displayVerificationPopover = (
    element: HTMLDivElement,
    content: number | VerificationMessages,
    shouldRefresh?: boolean
  ) => {
    showPopover(<VerificationPopover content={content} />, element);
    if (shouldRefresh) dataTableRef.current?.refresh();
  };

  const showPinPopup = (element: HTMLDivElement, user: UserDetailsDto) => {
    if (user.phoneConfirmed)
      authService
        .fetchAuthenticationPin(user.id)
        .then(response => displayVerificationPopover(element, response.data.pin))
        .catch(error => {
          if (error.response.status === 400) displayVerificationPopover(element, 'USER_NOT_EXISTS');
          else if (error.response.status === 404) displayVerificationPopover(element, 'NO_PIN');
          else handleError(error);
        });
    else
      userService
        .fetchPhoneActivationPin(user.id)
        .then(response => displayVerificationPopover(element, response.data.pin))
        .catch(error => {
          if (error.response.status === 400) displayVerificationPopover(element, 'USER_NOT_EXISTS');
          else if (error.response.status === 404) displayVerificationPopover(element, 'NO_PIN');
          else if (error.response.status === 409) displayVerificationPopover(element, 'PHONE_VERIFIED');
          else handleError(error);
        });
  };

  const confirmUserSignup = (element: HTMLDivElement, user: UserDetailsDto) => {
    userService
      .confirmSignup(user.id)
      .then(() => displayVerificationPopover(element, 'SUCCESSFULLY_CONFIRMED', true))
      .catch(error => {
        if (error.response.status === 404) displayVerificationPopover(element, 'USER_NOT_EXISTS');
        else if (error.response.status === 409) displayVerificationPopover(element, 'USER_ALREADY_CONFIRMED');
        else handleError(error);
      });
  };

  return (
    <PageContainer>
      <Container>
        <DataTableWithFilters
          ref={dataTableRef}
          columns={columns}
          filters={filters}
          onTableStateChanged={onTableFiltered}
          filterInitState={filterInitState}
        />
      </Container>
    </PageContainer>
  );
};

export default FindUser;
